import { z } from 'zod';
import { isoDaySchema as isoDate } from './dates';
import { paginatedSchema, paginationQuerySchema } from './pagination';

export const OBJECTIVE_STATUSES = ['PLANNED', 'IN_PROGRESS', 'AT_RISK', 'DONE', 'SUSPENDED'] as const;
export type ObjectiveStatusName = (typeof OBJECTIVE_STATUSES)[number];
export const OBJECTIVE_STATUS_LABELS: Record<ObjectiveStatusName, string> = {
  PLANNED: 'Planejado',
  IN_PROGRESS: 'Em andamento',
  AT_RISK: 'Em risco',
  DONE: 'Concluído',
  SUSPENDED: 'Suspenso',
};

export const OBJECTIVE_HORIZONS = ['SHORT', 'MEDIUM', 'LONG'] as const;
export type ObjectiveHorizonName = (typeof OBJECTIVE_HORIZONS)[number];
export const OBJECTIVE_HORIZON_LABELS: Record<ObjectiveHorizonName, string> = {
  SHORT: 'Curto prazo (até 1 ano)',
  MEDIUM: 'Médio prazo (1 a 3 anos)',
  LONG: 'Longo prazo (mais de 3 anos)',
};

/** Desdobramento organizacional (T-045, RF-20): corporativo → diretoria → gerência. */
export const OBJECTIVE_LEVELS = ['CORPORATE', 'DIRECTORATE', 'MANAGEMENT'] as const;
export type ObjectiveLevelName = (typeof OBJECTIVE_LEVELS)[number];
export const OBJECTIVE_LEVEL_LABELS: Record<ObjectiveLevelName, string> = {
  CORPORATE: 'Corporativo',
  DIRECTORATE: 'Diretoria',
  MANAGEMENT: 'Gerência',
};
/** Profundidade do nível: o objetivo-pai é sempre de nível acima (rank menor) — por isso não há ciclos. */
export const OBJECTIVE_LEVEL_RANK: Record<ObjectiveLevelName, number> = {
  CORPORATE: 0,
  DIRECTORATE: 1,
  MANAGEMENT: 2,
};
/** O objetivo de nível `parent` pode ser pai de um de nível `child`? */
export const canParent = (parent: ObjectiveLevelName, child: ObjectiveLevelName) =>
  OBJECTIVE_LEVEL_RANK[parent] < OBJECTIVE_LEVEL_RANK[child];

/** Relações entre objetivos; o desdobramento vertical é o objetivo-pai (não uma relação). */
export const OBJECTIVE_RELATION_TYPES = ['CAUSE_EFFECT', 'HORIZONTAL'] as const;
export type ObjectiveRelationTypeName = (typeof OBJECTIVE_RELATION_TYPES)[number];
export const OBJECTIVE_RELATION_LABELS: Record<ObjectiveRelationTypeName, string> = {
  CAUSE_EFFECT: 'Causa e efeito',
  HORIZONTAL: 'Relação horizontal',
};

const ref = z.object({ id: z.string().uuid(), name: z.string() });
/** Objetivo na outra ponta de uma relação (arquivado continua aparecendo: o histórico fica). */
const relatedRef = ref.extend({ archived: z.boolean() });

/** Objetivo na lista (com perspectiva, responsável e área já resolvidos). */
export const objectiveSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  perspective: ref,
  owner: ref.nullable(),
  department: ref.nullable(),
  horizon: z.enum(OBJECTIVE_HORIZONS),
  level: z.enum(OBJECTIVE_LEVELS),
  /** Objetivo-pai no desdobramento; null também quando o pai é de área fora do escopo (ver parentHidden). */
  parent: relatedRef.nullable(),
  /** Há pai, mas de área fora do escopo de quem vê: fica oculto e é mantido ao salvar (DEC-016). */
  parentHidden: z.boolean(),
  /** Objetivos que desdobram este (os visíveis para quem pede). */
  children: z.array(relatedRef.extend({ level: z.enum(OBJECTIVE_LEVELS) })),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  weight: z.number().nullable(),
  status: z.enum(OBJECTIVE_STATUSES),
  notes: z.string().nullable(),
  archivedAt: z.string().nullable(),
  /** Relações em que este objetivo é a origem ("contribui para", "relaciona-se com"). */
  relations: z.array(z.object({ type: z.enum(OBJECTIVE_RELATION_TYPES), objective: relatedRef })),
  /** Relações em que outro objetivo aponta para este. */
  relatedFrom: z.array(z.object({ type: z.enum(OBJECTIVE_RELATION_TYPES), objective: relatedRef })),
});
export type Objective = z.infer<typeof objectiveSchema>;
export const objectiveListSchema = paginatedSchema(objectiveSchema);

export const objectiveListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(100, 'Busca longa demais.').optional(),
  perspectiveId: z.string().uuid().optional(),
  departmentId: z.string().uuid().optional(),
  ownerId: z.string().uuid().optional(),
  level: z.enum(OBJECTIVE_LEVELS).optional(),
  status: z.enum(OBJECTIVE_STATUSES).optional(),
  archived: z.enum(['true', 'false']).optional(),
});
export type ObjectiveListQuery = z.input<typeof objectiveListQuerySchema>;

/** Peso do objetivo na perspectiva, em % (0–100, até duas casas). */
const objectiveWeight = z.coerce
  .number({ message: 'Informe o peso em números.' })
  .min(0, 'O peso vai de 0 a 100.')
  .max(100, 'O peso vai de 0 a 100.')
  .multipleOf(0.01, 'Use no máximo duas casas decimais.');

/**
 * Formulário completo do objetivo (criar e substituir com PUT): campo ausente volta ao padrão —
 * quem edita manda o objetivo inteiro.
 */
export const objectiveInputSchema = z
  .object({
    name: z.string().trim().min(3, 'Informe o nome do objetivo.').max(160, 'Nome longo demais.'),
    description: z
      .string()
      .trim()
      .max(2000, 'A descrição pode ter no máximo 2000 caracteres.')
      .nullable()
      .optional()
      .transform((v) => v || null),
    perspectiveId: z.string({ message: 'Escolha a perspectiva.' }).uuid('Escolha a perspectiva.'),
    ownerId: z
      .string()
      .uuid('Responsável inválido.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    departmentId: z
      .string()
      .uuid('Área inválida.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    horizon: z.enum(OBJECTIVE_HORIZONS).default('MEDIUM'),
    level: z.enum(OBJECTIVE_LEVELS).default('CORPORATE'),
    /** Objetivo-pai (nível acima). null com pai oculto (fora do escopo de quem grava) mantém o pai. */
    parentId: z
      .string()
      .uuid('Objetivo-pai inválido.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    startDate: isoDate
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    endDate: isoDate
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    weight: objectiveWeight
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    status: z.enum(OBJECTIVE_STATUSES).default('PLANNED'),
    notes: z
      .string()
      .trim()
      .max(2000, 'As observações podem ter no máximo 2000 caracteres.')
      .nullable()
      .optional()
      .transform((v) => v || null),
    /** Objetivos relacionados (este objetivo como origem). */
    relations: z
      .array(z.object({ objectiveId: z.string().uuid(), type: z.enum(OBJECTIVE_RELATION_TYPES) }))
      .max(30, 'No máximo 30 relações.')
      .default([]),
  })
  .refine((v) => !v.startDate || !v.endDate || v.startDate <= v.endDate, {
    message: 'A data final precisa ser igual ou posterior à inicial.',
    path: ['endDate'],
  })
  .refine((v) => v.level === 'CORPORATE' || v.departmentId !== null, {
    message: 'Objetivos de diretoria e de gerência pertencem a uma área: escolha a área.',
    path: ['departmentId'],
  })
  .refine((v) => v.level !== 'CORPORATE' || v.parentId === null, {
    message: 'Objetivo corporativo é o topo do desdobramento: não tem objetivo-pai.',
    path: ['parentId'],
  });
export type ObjectiveInput = z.input<typeof objectiveInputSchema>;

/** Relação acrescentada pelo quadro do BSC (este objetivo como origem). */
export const objectiveRelationInputSchema = z.object({
  objectiveId: z.string({ message: 'Escolha o objetivo.' }).uuid('Escolha o objetivo.'),
  type: z.enum(OBJECTIVE_RELATION_TYPES),
  /** Tela de origem, para a auditoria (quadro do BSC ou mapa estratégico). */
  via: z.enum(['bsc_board', 'strategy_map']).optional(),
});
export type ObjectiveRelationInput = z.infer<typeof objectiveRelationInputSchema>;

/** Peso definido no próprio quadro do BSC (null = sem peso). */
export const objectiveWeightInputSchema = z.object({ weight: objectiveWeight.nullable() });

/**
 * Nova ordem dos objetivos ativos de uma perspectiva (os que a pessoa enxerga, do primeiro ao último;
 * os de fora do escopo dela ficam nas mesmas posições).
 */
export const objectiveOrderSchema = z.object({
  perspectiveId: z.string().uuid(),
  ids: z
    .array(z.string().uuid())
    .min(1)
    .max(500)
    .refine((ids) => new Set(ids).size === ids.length, 'Objetivo repetido na ordem.'),
});

/** Soma dos pesos dos objetivos de uma perspectiva (para o aviso de "a soma deveria ser 100%"). */
export function objectiveWeightTotal(items: Pick<Objective, 'weight'>[]): number {
  return Math.round(items.reduce((sum, o) => sum + (o.weight ?? 0), 0) * 100) / 100;
}

/**
 * Opções do formulário: perspectivas ativas, áreas ativas (no escopo de quem pede), pessoas da empresa
 * e objetivos ativos do projeto (também no escopo).
 */
export const objectiveOptionsSchema = z.object({
  perspectives: z.array(ref),
  departments: z.array(ref.extend({ parentId: z.string().uuid().nullable() })),
  owners: z.array(ref),
  objectives: z.array(
    ref.extend({
      perspectiveId: z.string().uuid(),
      level: z.enum(OBJECTIVE_LEVELS),
      parentId: z.string().uuid().nullable(),
    }),
  ),
});
export type ObjectiveOptions = z.infer<typeof objectiveOptionsSchema>;

// ---------------------------------------------------------------------------------------------
// Detalhe do objetivo: alterações pela trilha de auditoria (T-046a, RF-19; DEC-052).

export const objectiveChangeSchema = z.object({
  id: z.string(),
  occurredAt: z.string(),
  actorName: z.string().nullable(),
  action: z.string(),
  field: z.string().nullable(),
  /** Nome do campo e os valores como a pessoa lê (rótulos, nomes no lugar de ids, datas e números). */
  fieldLabel: z.string().nullable(),
  oldDisplay: z.string().nullable(),
  newDisplay: z.string().nullable(),
  /** Origem (tela, importação, sistema…) e motivo, quando houver. */
  source: z.string(),
  reason: z.string().nullable(),
  /** Por onde veio a alteração (ex.: quadro de construção do BSC); null pela tela do objetivo. */
  via: z.string().nullable(),
});
export type ObjectiveChange = z.infer<typeof objectiveChangeSchema>;

export const objectiveChangesQuerySchema = z.object({
  /** Cursor: "occurredAt|id" do último item da página anterior. */
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export const objectiveChangesSchema = z.object({
  items: z.array(objectiveChangeSchema),
  nextCursor: z.string().nullable(),
});
export type ObjectiveChanges = z.infer<typeof objectiveChangesSchema>;
